import { FormControl, InputLabel, Select, selectClasses } from '@mui/material';
import type { InvoiceDate } from '@weft/types';
import type { Nullable, ValueOf } from '@weft/utils';
import { dayjs } from '@weft/utils';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { MONTHS_LABELS } from '../../../pages/customers/CustomerOverviewPage/customer-view-page-components/BillingScheduleChart/billing-schedule-chart-consts';
import { ArrowIcon } from '../../icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../icons/ArrowIcon/types/arrow-icon-direction';
import { SelectedIcon } from '../../icons/SelectedIcon';

type CalendarDropdownProps = {
  onChange?: (dates: InvoiceDate[], year: number) => void;
  defaultSelectedMonths: { month: string, year:number }[];
  defaultSelectedYear: Nullable<number>;
};

export const CalendarDropdown:React.FC<CalendarDropdownProps> = ({
  onChange,
  defaultSelectedMonths,
  defaultSelectedYear,
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(defaultSelectedYear ?? dayjs().year());
  const [selectedDates, setSelectedDates] = useState<InvoiceDate[]>(defaultSelectedMonths ?? []);

  const handleMonthSelect = useCallback((selectedMonth: string) => {
    setSelectedDates((prev) => {
      const updatedDates = getUpdatedDates(prev, selectedMonth);

      onChange?.(updatedDates, selectedYear);

      return updatedDates;
    });
  }, [selectedYear]);

  function getUpdatedDates(prevSelectedDates: InvoiceDate[], selectedMonth: string) {
    if (isDateSelected(selectedMonth, selectedYear, prevSelectedDates)) {
      return prevSelectedDates.filter(date => !(date.year === selectedYear && date.month === selectedMonth));
    }

    return [...prevSelectedDates, { month: selectedMonth, year: selectedYear }];
  }

  function isDateSelected(month: string, year: number, dates: InvoiceDate[]) {
    return dates.some((date) => date.month === month && date.year === year);
  }

  function selectEntireYear() {
    const allMonthsSelected = MONTHS_LABELS.every((month) => isDateSelected(month, selectedYear, selectedDates));

    if (allMonthsSelected) {
      setSelectedDates([]);

      onChange?.([], selectedYear);

      return;
    }

    setSelectedDates(MONTHS_LABELS.map((month) => ({ month, year: selectedYear })));

    onChange?.(MONTHS_LABELS.map((month) => ({ month, year: selectedYear })), selectedYear);
  }

  const handleYearChange = useCallback((action: YearChangeType) => {
    setSelectedYear((prev) => {
      const updatedYear = action === YEAR_CHANGE_TYPES.INCREMENT ? prev + 1 : prev - 1;

      return updatedYear;
    });
  }, [selectedDates]);

  return (
    <FormControl sx={{
      height: '100%',
    }}
    >
      <InputLabel sx={{
        position: 'unset',
        transform: 'unset',
        fontSize: '12px',
      }}
      >
        Date
      </InputLabel>

      <Select
        value={selectedDates}
        variant="filled"
        multiple
        sx={{
          marginTop: 'auto',
          height: '46px',
          width: '180px',
          padding: 0,
          borderRadius: '8px',
          [`& .${selectClasses.multiple}`]: {
            background: 'transparent !important',
          },
        }}
        MenuProps={{
          classes: {
            paper: 'rounded-xl mt-2',
          },
        }}
        renderValue={(selected) => (
          <div className="flex gap-2 truncate pl-1">
            {selected.map((date, index) => (
              <div
                key={index}
                className="flex items-center gap-2"
              >
                <div>{date.month}</div>
                <div>{date.year}</div>
              </div>
            ))}
          </div>
        )}
      >
        <div className="p-4">
          <div className="border-b-line mb-3 flex items-center justify-between border-b pb-3">
            <ArrowIcon
              onClick={() => handleYearChange(YEAR_CHANGE_TYPES.DECREMENT)}
              className="text-style cursor-pointer"
              direction={ARROW_ICON_DIRECTIONS.LEFT}
            />
            <button onClick={selectEntireYear}>{selectedYear}</button>
            <ArrowIcon
              onClick={() => handleYearChange(YEAR_CHANGE_TYPES.INCREMENT)}
              className="text-style cursor-pointer"
              direction={ARROW_ICON_DIRECTIONS.RIGHT}
            />
          </div>
          <div className="grid grid-cols-4 grid-rows-3 gap-2">
            {MONTHS_LABELS.map((month, index) => {
              const isSelected = isDateSelected(month, selectedYear, selectedDates);

              return (
                <div
                  key={index}
                  onClick={() => handleMonthSelect(month)}
                  className={classNames(
                    'flex cursor-pointer items-center justify-center rounded	border border-style px-4 py-2 text-style',
                    { 'bg-style text-white relative': isSelected },
                    { 'hover:border-black hover:text-black': !isSelected },
                  )}
                >
                  {isSelected && <div className="absolute -right-2.5 -top-1.5"><SelectedIcon/></div>}
                  <div>{month}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Select>
    </FormControl>
  );
};

const YEAR_CHANGE_TYPES = {
  INCREMENT: 'increment',
  DECREMENT: 'decrement',
} as const;

export type YearChangeType = ValueOf<typeof YEAR_CHANGE_TYPES>;